import {
  getMcpToolTextOutput,
  isForgeDiscoveryEmptyOutput,
  parseMcpToolJsonOutput,
} from '@erikdakoda/ai-tool/shared/parseMcpToolTextOutput';

type ToolCallWithOutput = {
  readonly input?: unknown;
  readonly output?: unknown;
};

function getInputString(input: unknown, key: string): string | undefined {
  if (typeof input !== 'object' || input === null || !(key in input)) {
    return undefined;
  }
  const value = (input as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Adds skills scope text for `list_skills` output labels (counts come from structured JSON). */
export function enrichForgeDiscoveryListSkills(
  toolCall: ToolCallWithOutput,
): Record<string, unknown> {
  const department = getInputString(toolCall.input, 'department');
  if (department) {
    return { skillsScope: `in \`${department}\`` };
  }

  const parsed = parseMcpToolJsonOutput(toolCall.output);
  if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
    const deptCount = (parsed as { deptCount?: unknown }).deptCount;
    if (typeof deptCount === 'number' && deptCount > 0) {
      return { skillsScope: `across ${deptCount} departments` };
    }
  }

  return { skillsScope: 'across departments' };
}

/** Marks not-found `get_project` responses for noop labels. */
export function enrichForgeDiscoveryGetProject(
  toolCall: ToolCallWithOutput,
): Record<string, unknown> {
  if (isForgeDiscoveryEmptyOutput(toolCall.output)) {
    return { discoveryEmpty: true };
  }
  return {};
}

/** Marks successful `get_skill` loads for output labels. */
export function enrichForgeDiscoveryGetSkill(
  toolCall: ToolCallWithOutput,
): Record<string, unknown> {
  const department = getInputString(toolCall.input, 'department');
  const departmentScope = department ? ` in \`${department}\`` : '';

  if (isForgeDiscoveryEmptyOutput(toolCall.output)) {
    return { discoveryEmpty: true, skillScope: departmentScope };
  }

  const parsed = parseMcpToolJsonOutput(toolCall.output);
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    !Array.isArray(parsed) &&
    (parsed as { found?: unknown }).found === true
  ) {
    return { skillLoaded: true, skillScope: departmentScope };
  }

  const text = getMcpToolTextOutput(toolCall.output);
  if (text !== undefined && text.length > 0) {
    return { skillLoaded: true, skillScope: departmentScope };
  }

  return { skillScope: departmentScope };
}
